import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle2, XCircle, ArrowLeft } from 'lucide-react';

export const Route = createFileRoute('/_dashboard/integrations/callback')({
  component: OAuthCallbackPage,
  validateSearch: (search: Record<string, unknown>) => ({
    success: search.success as string | undefined,
    error: search.error as string | undefined,
    provider: search.provider as string | undefined,
    connection_id: search.connection_id as string | undefined,
  }),
});

type CallbackStatus = 'processing' | 'success' | 'error';

function OAuthCallbackPage() {
  const navigate = useNavigate();
  const searchParams = Route.useSearch();
  const [status, setStatus] = useState<CallbackStatus>('processing');
  const [message, setMessage] = useState('');
  const [isPopup, setIsPopup] = useState(false);

  useEffect(() => {
    setIsPopup(!!window.opener);

    const { success, error, provider, connection_id: connectionId } = searchParams;

    if (error) {
      setStatus('error');
      setMessage(decodeURIComponent(error));
      return;
    }

    if (success === 'true') {
      setStatus('success');
      setMessage(
        provider
          ? `Successfully connected to ${provider}`
          : 'Connection established successfully'
      );

      // If we're in a popup, try to notify the parent window
      if (window.opener) {
        try {
          window.opener.postMessage(
            {
              type: 'oauth-callback',
              success: true,
              provider,
              connectionId,
            },
            window.location.origin
          );
          // Close popup after a short delay
          setTimeout(() => window.close(), 1500);
        } catch {
          // Parent window might be closed or different origin
        }
      }
      return;
    }

    // If no success or error, something went wrong
    setStatus('error');
    setMessage('Invalid callback. Please try again.');
  }, [searchParams]);

  const handleGoBack = () => {
    if (window.opener) {
      window.close();
    } else {
      navigate({ to: '/integrations' });
    }
  };

  return (
    <div className="flex items-center justify-center min-h-[50vh]">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="flex items-center justify-center gap-2">
            {status === 'processing' && (
              <>
                <Loader2 className="size-5 animate-spin" />
                Processing...
              </>
            )}
            {status === 'success' && (
              <>
                <CheckCircle2 className="size-5 text-green-500" />
                Connected
              </>
            )}
            {status === 'error' && (
              <>
                <XCircle className="size-5 text-destructive" />
                Connection Failed
              </>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          <p className="text-muted-foreground">{message}</p>
          {status !== 'processing' && (
            <Button onClick={handleGoBack}>
              <ArrowLeft className="mr-2 size-4" />
              {isPopup ? 'Close Window' : 'Back to Integrations'}
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
